/* eslint-disable react/require-default-props */
import React, { ChangeEventHandler, useState } from 'react';
import { Form, Formik } from 'formik';
import ReactModal from 'react-modal';
import * as yup from 'yup';
import { AlertAdapter, CommonDatePicker, RegularButton } from 'pg-ui-library';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import closeSvg from 'src/assets/close.svg';
import { t } from 'i18next';
import { IQleNewHireEnrollmentEvent } from 'src/interfaces';
import BenefitsService from 'src/services/benefits.service';
import {
  CloseIcon,
  EnrollDatePickerValidation,
  EnrollmentFormContent,
  EnrollmentFormContentTitle,
  EnrollmentFormTitle,
  FooterButtonWrapper,
  FormTitle,
  FormWrapper,
  InputLabel,
  InputWrapper,
} from './styles';

import './index.css';
import ConfirmationModal from '../confirmation-modal';

type EnrollmentFormType = {
  planInitialPeriod: Date;
  planEndPeriod: Date;
};

const EnrollmentFormStepOne: React.FC<{
  values: EnrollmentFormType;
  handleChange: ChangeEventHandler<HTMLInputElement> | ChangeEventHandler<HTMLTextAreaElement>;
}> = ({ values, handleChange }) => (
  <>
    <span>{t('benefits.newHireQleSetup.modal.text1')}</span>
    <FormWrapper>
      <div style={{ display: 'flex', gap: '40px', marginTop: '20px' }}>
        <InputWrapper>
          <InputLabel>{t('benefits.newHireQleSetup.modal.startDate')}</InputLabel>
          <CommonDatePicker
            id="planInitialPeriod"
            name="planInitialPeriod"
            label=""
            onChange={handleChange}
            placeholderText="MM/DD/YY"
          />
          <div style={{ position: 'absolute', marginTop: '7%' }}>
            {values.planInitialPeriod > values.planEndPeriod ? (
              <EnrollDatePickerValidation style={{ color: 'red', fontSize: '8pt' }}>
                {t('openEnrollment.modal.periodStart')}
              </EnrollDatePickerValidation>
            ) : (
              ''
            )}
          </div>
          <InputLabel>{t('benefits.newHireQleSetup.modal.endDate')}</InputLabel>
          <CommonDatePicker
            id="planEndPeriod"
            name="planEndPeriod"
            label=""
            onChange={handleChange}
            placeholderText="MM/DD/YY"
          />
          <div
            style={{
              position: 'absolute',
              marginTop: '7%',
              marginLeft: '48.7%',
            }}
          >
            {values.planEndPeriod < values.planInitialPeriod ? (
              <EnrollDatePickerValidation>{t('openEnrollment.modal.periodEnd')}</EnrollDatePickerValidation>
            ) : (
              ''
            )}
          </div>
        </InputWrapper>
      </div>
    </FormWrapper>
  </>
);

export const QleNewHireEnrollmentModal: React.FC<{
  data?: IQleNewHireEnrollmentEvent;
  isOpen: boolean;
  handleModal: () => void;
}> = ({ data, isOpen, handleModal }) => {
  const [openConfirmModal, setOpenConfirmModal] = useState(false);

  const queryClient = useQueryClient();

  const schema = yup.object({
    planInitialPeriod: yup.date().required(t('global.form.required')),
    planEndPeriod: yup.date().required(t('global.form.required')),
  });

  const mutationCreate = useMutation(
    (variables: EnrollmentFormType) =>
      BenefitsService.putBenefitsQleNewHirePriceSetup({
        id: data?.id ?? undefined,
        planInitialPeriod: variables.planInitialPeriod.toISOString(),
        planEndPeriod: variables.planEndPeriod.toISOString(),
      }),

    {
      onSuccess: () => {
        AlertAdapter('Price setup updated', 'success');
        queryClient.invalidateQueries(['benefits/qle-new-hire/price-setup']);
        handleModal();
      },
      onError: () => {
        AlertAdapter('Failed to create', 'error');
      },
    }
  );

  const onSubmit = async (values: EnrollmentFormType) => {
    setOpenConfirmModal(false);
    await mutationCreate.mutateAsync(values);
  };

  const onConfirm = async () => {
    setOpenConfirmModal(true);
  };

  return (
    <ReactModal
      isOpen={isOpen}
      ariaHideApp={false}
      style={{
        overlay: {
          margin: 'auto',
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
        },
        content: {
          borderRadius: '5px',
          margin: 'auto',
          width: '900px',
          height: '574px',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#f3f4f8',
        },
      }}
    >
      <EnrollmentFormTitle>
        <FormTitle>{t('benefits.qleNewHireEnrollment.title')}</FormTitle>
        <div>
          <CloseIcon src={closeSvg} onClick={handleModal} />
        </div>
      </EnrollmentFormTitle>
      <EnrollmentFormContent>
        <EnrollmentFormContentTitle>{t('benefits.newHireQleSetup.modal.title')}</EnrollmentFormContentTitle>

        <Formik
          initialValues={{
            planInitialPeriod: data?.planInitialPeriod ? new Date(data?.planInitialPeriod as string) : new Date(),
            planEndPeriod: data?.planEndPeriod ? new Date(data?.planEndPeriod as string) : new Date(),
          }}
          validationSchema={schema}
          onSubmit={onConfirm}
        >
          {({ handleChange, isSubmitting, values, isValid }) => (
            <>
              <Form
                style={{
                  display: 'flex',
                  flex: '1',
                  flexDirection: 'column',
                  flexWrap: 'wrap',
                  alignContent: 'space-between',
                }}
              >
                <EnrollmentFormStepOne values={values} handleChange={handleChange} />
                <FooterButtonWrapper>
                  <div>
                    <RegularButton
                      data-cy="enrollment-cancel-button"
                      type="button"
                      disabled={isSubmitting || mutationCreate.isLoading}
                      onClick={() => handleModal()}
                    >
                      {t('global.form.cancel')}
                    </RegularButton>
                  </div>
                  <div>
                    <RegularButton
                      data-cy="enrollment-submit-button"
                      type="button"
                      disabled={isSubmitting || mutationCreate.isLoading}
                      onClick={() => {
                        if (!isValid) {
                          AlertAdapter('You need to fill all form fields', 'warning');
                          return;
                        }
                        onConfirm();
                      }}
                    >
                      {t('global.form.save')}
                    </RegularButton>
                  </div>
                </FooterButtonWrapper>
              </Form>
              <ConfirmationModal
                message="Changing the new hire or QLE price date range will impact future enrollment prices. Confirm?"
                isOpen={openConfirmModal}
                onCancel={() => setOpenConfirmModal(false)}
                onConfirm={() => onSubmit(values)}
              />
            </>
          )}
        </Formik>
      </EnrollmentFormContent>
    </ReactModal>
  );
};
